'''<script setup>
import { onMounted, ref, defineAsyncComponent } from 'vue';
import { useRecaptcha } from '@/composables/useRecaptcha';
import ReCaptchaV3 from './items/ReCaptchaV3.vue';
import SubmitButton from './items/SubmitButton.vue';
import { useRuntimeConfig } from '#app';

const config = useRuntimeConfig();

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
});

const fields = ref([]);
const submitData = ref({});
const formData = ref({});
const isLoading = ref(true);
const error = ref(null);
const submissionMessage = ref('');
const submissionError = ref('');
const submissionStatus = ref(''); // Added for better state management
const submittedData = ref(null); // Added for better state management
const isSubmitting = ref(false);

const fieldComponents = {
  name: defineAsyncComponent(() => import('./items/NameField.vue')),
  email: defineAsyncComponent(() => import('./items/EmailField.vue')),
  phone: defineAsyncComponent(() => import('./items/PhoneField.vue')),
  textarea: defineAsyncComponent(() => import('./items/TextareaField.vue')),
  text: defineAsyncComponent(() => import('./items/TextField.vue')),
};

const isValidated = ref(false)

const getFieldComponent = (type) => {
  return fieldComponents[type] || fieldComponents.text;
};

// --- reCAPTCHA v3 Integration ---
const RECAPTCHA_SITE_KEY = config.public.recaptchaSiteKey;
const { getToken } = useRecaptcha(RECAPTCHA_SITE_KEY);

// Fetches the form structure from the custom API
async function fetchFormStructure() {
  const apiUrl = config.public.wordpressApiUrl;
  if (!props.id) return;
  isLoading.value = true;
  error.value = null;
  try {
    const response = await fetch(`${apiUrl}/wp-json/custom-forminator/v1/form/${props.id}`);
    if (!response.ok) {
      throw new Error(`Erreur lors du chargement de la structure du formulaire (statut: ${response.status})`);
    }
    const data = await response.json();
    fields.value = data.fields;
    submitData.value = data.submit_button || {};
    // Initialize formData with keys based on field IDs
    data.fields.forEach(field => {
      if (field.type === 'name') {
        formData.value[field.id] = {};
      } else {
        formData.value[field.id] = '';
      }
    });
  } catch (e) {
    console.error("Erreur de chargement du formulaire:", e);
    error.value = e.message;
  }
}

// Handles the form submission
async function handleSubmit() {
  isSubmitting.value = true;
  submissionMessage.value = '';
  submissionError.value = '';

  try {
    let token = '';
    
    if (RECAPTCHA_SITE_KEY) { // Only attempt to get token if key is present
      token = await getToken('form_submit');
    } else {
      console.log('RECAPTCHA_SITE_KEY is not set, skipping token request.');
    }
    
    // 2. Prepare data for submission
    const submissionFormData = new FormData();

    for (const fieldId in formData.value) {
      const value = formData.value[fieldId];
      const field = fields.value.find(f => f.id === fieldId);

      if (field) {
        if (field.type === 'name') {
          if (value.fname) {
            submissionFormData.append(`${fieldId}-first-name`, value.fname);
          }
          if (value.lname) {
            submissionFormData.append(`${fieldId}-last-name`, value.lname);
          }
        } else {
          submissionFormData.append(fieldId, value);
        }
      }
    }
    
    isValidated.value = true;
    // Add reCAPTCHA token and referer_url as direct properties
    submissionFormData.append('form_id', props.id);
    submissionFormData.append('g-recaptcha-response', token);
    submissionFormData.append('referer_url', window.location.href);

    // 3. Submit the form
    const apiUrl = config.public.wordpressApiUrl;
    const response = await fetch(`${apiUrl}/wp-json/custom-forminator/v1/form/${props.id}/submit`, {
      method: 'POST',
      body: submissionFormData,
    });

    const result = await response.json();

    if (result.success) {
      submissionStatus.value = 'success';
      submittedData.value = result.data;
      submissionMessage.value = result.data.message || 'Formulaire soumis avec succès!';
    } else {
      submissionStatus.value = 'error';
      console.log(result);
      submissionError.value = result.message || 'An unknown error occurred.';
    }
  } catch (err) {
    submissionStatus.value = 'error';
    submissionError.value = err.message || 'A network error occurred.';
  } finally {
    isSubmitting.value = false;
    isValidated.value = true;
  }
}

onMounted(async () => {
  isLoading.value = true;
  try {
    await fetchFormStructure();
  } catch (e) {
    // Error is already set by the fetching function
  } finally {
    isLoading.value = false;
  }
});
</script>


<template>
  <div class="forminator-form-container">
    <div id="formulaire"></div>
     <Transition name="fade"
       mode="in-out"
      >
      <div v-if="isLoading" class="loading-message">Chargement du formulaire...</div>
      <div v-else-if="isValidated" class="loading-message">{{ submissionMessage }}</div>
      
      <div v-else-if="error" class="error-message">
        <p>{{ error }}</p>
      </div>
      <form v-else @submit.prevent="handleSubmit" class="dynamic-form">
        <div v-for="field in fields" :key="field.id" class="form-field" :class="'field-cols-' + field.cols">
          <component
            v-if="!['hidden', 'captcha'].includes(field.type)"
            :is="getFieldComponent(field.type)"
            :field="field"
            v-model="formData[field.id]"
          />
        </div>

        <div class="form-submission">
          <SubmitButton :is-submitting="isSubmitting" :label="submitData.label" v-if="!isValidated" />
          <p v-if="submissionMessage" class="success-message">{{ submissionMessage }}</p>
          <p v-if="submissionError" class="error-message">{{ submitData.invalid_message }}</p>
          <ReCaptchaV3 :error-message="submissionError" />
        </div>

      </form>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.forminator-form-container {
  padding: 65px 35px 75px;
  width: 100%;
  max-width: 800px;
  flex-shrink: 0;
  border-radius: 25px;
  border: 1px solid #193452;
  background: rgba(15, 21, 37, 0.70);
  backdrop-filter: blur(10px);
  position: relative;

  & >  #formulaire {
    position: absolute;
    top: -100px;
  }

}

.dynamic-form {
  display: flex;
  flex-wrap: wrap; /* Allow items to wrap to the next line */
  gap: 35px; /* Gap between fields */
}

.form-field {
  display: flex;
  flex-direction: column;
  /* Default to full width for mobile-first approach */
  width: 100%;
}

.form-submission {
  width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: all .35s ease-out ;
}

.fade-enter-from,
.fade-leave-to {
  filter: blur(10px);
  opacity: 0;
}

/* Grid system for larger screens (e.g., tablets and desktops) */
@media (min-width: 768px) {
  .form-field.field-cols-1 {
    width: calc(100% / 12 * 1);
  }
  .form-field.field-cols-2 {
    width: calc(100% / 12 * 2);
  }
  .form-field.field-cols-3 {
    width: calc(100% / 12 * 3);
  }
  .form-field.field-cols-4 {
    width: calc(100% / 12 * 4);
  }
  .form-field.field-cols-5 {
    width: calc(100% / 12 * 5);
  }
  .form-field.field-cols-6 {
    width: calc((100% - 40px) / 12 * 6); /* 50% */
  }
  .form-field.field-cols-7 {
    width: calc(100% / 12 * 7);
  }
  .form-field.field-cols-8 {
    width: calc(100% / 12 * 8);
  }
  .form-field.field-cols-9 {
    width: calc(100% / 12 * 9);
  }
  .form-field.field-cols-10 {
    width: calc(100% / 12 * 10);
  }
  .form-field.field-cols-11 {
    width: calc(100% / 12 * 11);
  }
  .form-field.field-cols-12 {
    width: 100%; /* 12/12 */
  }

  .forminator-form-container {
    padding: 65px 80px 75px;

  }
}

</style>